import React, { FC, PropsWithChildren } from 'react';
import Link from 'next/link';
import { useChatData } from '@/containers/ChatContainer';
import { Spinner } from '@mofid-securities/uikit';
import { Icon, TIconName } from '@/components/atomic/Icon';
import BottomSheet from '@/components/BottomSheet';
import { Divider } from '@/components/Divider';
import Button from '@/common/components/Button';
import {
  IUserStatusOnClickWrapperProps,
  UserStatusMaybeButtonOrLink,
} from '@/userStatus/components';
import { classnames } from '@/common/utils/classnames';
import { IStory } from '@/components/Story/Story.type';
import { ConsultationRequestBottomSheet } from '../../ConsultationRequestBottomSheet/ConsultationRequestBottomSheet';
import { HintThumbNails } from './HintThumbnail';

interface IInfoSheetProps {
  items: IStory[];
  isLoading: boolean;
}

type TInfoSheetItem = {
  title: string;
  icon: TIconName;
  onClick?: (__param: any) => void;
  href?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  forbiddenStatuses?: IUserStatusOnClickWrapperProps['forbiddenStatuses'];
};

export const InfoSheet: FC<IInfoSheetProps> = ({ items, isLoading }) => {
  const chatData = useChatData();

  const infoItems: TInfoSheetItem[] = [
    {
      title: 'پشتیبانی هوشمند',
      icon: 'ai_support',
      isDisabled: !chatData.isEnabled,
      isLoading: chatData.isLoading,
      onClick: () => chatData.toggleVisibility(),
    },
    {
      title: 'تماس با ما',
      icon: 'call',
      href: 'tel:0218700',
    },
    {
      title: 'ثبت درخواست مشاوره',
      icon: 'user_contact_outline',
      onClick: () => {
        BottomSheet.open({ children: <ConsultationRequestBottomSheet /> });
      },
      forbiddenStatuses: ['GUEST', 'ANONYMOUS'],
    },
  ];

  return (
    <div className="flex flex-col gap-3 p-5 pb-8">
      <HintThumbNails isLoading={isLoading} items={items} />
      {infoItems?.map((item) => (
        <UserStatusMaybeButtonOrLink
          key={item?.title}
          forbiddenStatuses={item?.forbiddenStatuses}
          onClick={item?.onClick}
        >
          <ItemContainer
            className={classnames(
              'flex cursor-pointer items-center justify-between p-4',
              item.isDisabled && 'pointer-events-none opacity-60'
            )}
            href={item?.href}
          >
            {item?.isLoading ? (
              <Spinner />
            ) : (
              <Icon color="#282B2E" icon={item?.icon} size={26} />
            )}
            <span className="ml-auto  mr-3 text-sm font-medium text-gray-120">
              {item?.title}
            </span>
            <Icon color="#A5ADB8" icon="chevron_left" size={18} />
          </ItemContainer>
          <Divider />
        </UserStatusMaybeButtonOrLink>
      ))}
      <Button variant="link" onClick={BottomSheet.close}>
        بازگشت
      </Button>
    </div>
  );
};

const ItemContainer: FC<
  PropsWithChildren<
    Pick<TInfoSheetItem, 'href' | 'onClick'> & { className?: string }
  >
> = ({ children, className, onClick, href }) => (
  <>
    {href ? (
      <Link className={className} href={href}>
        {children}
      </Link>
    ) : (
      <div className={className} onClick={onClick}>
        {children}
      </div>
    )}
  </>
);
